import { useQuery } from '@tanstack/react-query';
import { useBaseTopicRequestBuilder } from '../../utils/useBaseTopicRequestBuilder';
import { TopicRequestBuilder } from '../../utils/TopicRequestBuilder';
import { useAppSelector } from 'src/utils/Hooks';
import axios from 'axios';
import { TopicAdSummaryMetrics } from './types';
import { topicTrafficServerProxy, EntityMetadataType, getEntityIdToNameMap } from '../../serverProxy';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';

export interface GetAdMetricsByDimensionResponseRecord extends TopicAdSummaryMetrics {
  dimension: string;
}

interface GetAdMetricsByDimensionResponse {
  records: GetAdMetricsByDimensionResponseRecord[];
}

const entityTypeToMetadataTypeMap: Record<string, EntityMetadataType> = {
  campaignId: 'adCampaign',
  stacklineSku: 'product',
  subcategoryId: 'subcategory',
  brandId: 'brand',
  categoryId: 'category',
  entityId: 'adEntity'
};

/**
 * Returns all the metrics for topics paid search page
 * double dropdown bar chart
 */
export function useAdMetricsByDimension(sortField: string, dimension: string) {
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const retailerId = useAppSelector((state) => state.retailer.id);
  const entityType = useMainEntityType();

  const baseRequest = useBaseTopicRequestBuilder();
  const metricsByDimensionRequest = baseRequest
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .setDimension(dimension)
    .setSortField(sortField)
    .build();

  const metricsByDimensionComparisonRequest = new TopicRequestBuilder(metricsByDimensionRequest)
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek)
    .build();

  const adMetricsQueryKey =
    entityType === 'topicgroup' ? 'GetTopicGroupAdMetricsByDimension' : 'GetAdMetricsByDimension';
  const { data, isLoading } = useQuery({
    queryKey: [TOPIC_QUERY_KEYS.all, adMetricsQueryKey, metricsByDimensionRequest, metricsByDimensionComparisonRequest],
    queryFn: async () => {
      const endpoint = `api/beacontopics/${adMetricsQueryKey}`;
      const [{ data: response }, { data: comparisonResponse }] = await Promise.all([
        axios.post<GetAdMetricsByDimensionResponse>(endpoint, metricsByDimensionRequest),
        axios.post<GetAdMetricsByDimensionResponse>(endpoint, metricsByDimensionComparisonRequest)
      ]);

      if (dimension === 'searchKeyword') {
        return [
          { ...response, records: response.records.map((record) => ({ ...record, name: record.dimension })) },
          {
            ...comparisonResponse,
            records: comparisonResponse.records.map((record) => ({ ...record, name: record.dimension }))
          }
        ];
      }

      const dimensions = response.records.map((record) => record.dimension);
      const rows = await topicTrafficServerProxy.getEntityMetadata({
        retailerId: +retailerId,
        entityType: entityTypeToMetadataTypeMap[dimension],
        entityIds: dimensions
      });
      const idToNameMap = getEntityIdToNameMap(rows);

      return [
        {
          ...response,
          records: response.records.map((record) => ({
            ...record,
            name: idToNameMap.get(record.dimension)?.name ?? record.dimension,
            fullRecord: idToNameMap.get(record.dimension)?.record
          }))
        },
        {
          ...comparisonResponse,
          records: comparisonResponse.records.map((record) => ({
            ...record,
            name: idToNameMap.get(record.dimension)?.name ?? record.dimension,
            fullRecord: idToNameMap.get(record.dimension)?.record
          }))
        }
      ];
    }
  });

  const response = data ?? [{ records: [] }, { records: [] }];

  return {
    response,
    isLoading
  };
}
